import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  FaSpinner, FaArrowLeft, FaClock, FaCheck, FaExclamationTriangle, 
  FaEnvelope, FaUserPlus, FaLock, FaFileAlt, FaClipboardList, 
  FaShippingFast, FaThumbsUp, FaCommentDots, FaPhone, FaCalendarAlt, 
  FaUser, FaEye, FaEyeSlash, FaInfoCircle, FaExchangeAlt
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { getQuotationByMagicLink, verifyMagicLink, convertQuotationToAccount } from '../services/quotationAuthService';
import { updateQuotationStatusWithNotification } from '../../../services/email/quotationEmailService';
import '../styles/timeline.css';

const STATUS_COLORS = {
  pending: 'warning',
  en_proceso: 'info',
  estimado_listo: 'info',
  aprobada: 'success',
  completada: 'success',
  rechazada: 'danger'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  en_proceso: 'En Proceso',
  estimado_listo: 'Estimado Listo',
  aprobada: 'Aprobada',
  completada: 'Completada',
  rechazada: 'Rechazada'
};

const STATUS_ICONS = {
  pending: FaClipboardList,
  en_proceso: FaFileAlt,
  estimado_listo: FaCheck,
  aprobada: FaThumbsUp,
  completada: FaShippingFast,
  rechazada: FaExclamationTriangle
};

export default function QuotationDetailPage() {
  const { magicLink } = useParams();
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [accountData, setAccountData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: ''
  });
  const [accountError, setAccountError] = useState(null);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Función para obtener los detalles de la cotización
  const fetchQuotationDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Obteniendo detalles de cotización con magic link:', magicLink);
      
      // Verificar si el magic link es válido
      const verification = await verifyMagicLink(magicLink);
      
      if (!verification.isValid) {
        throw new Error('El enlace de seguimiento no es válido o ha expirado');
      }
      
      const result = await getQuotationByMagicLink(magicLink);
      
      // Si el usuario está autenticado, verificar permisos
      if (isAuthenticated && user) {
        const isAdmin = user.get('role') === 'admin' || user.get('isAdmin') === true;
        const quotationEmail = result.get('userEmail');
        const userEmail = user.get('email');
        
        console.log('Verificando permisos:', {
          isAdmin,
          quotationEmail,
          userEmail,
          match: quotationEmail === userEmail
        });
        
        if (!isAdmin && quotationEmail !== userEmail) {
          throw new Error('No tienes permiso para ver esta cotización');
        }
      } else {
        // Si no está autenticado, mostrar opción para crear cuenta
        // solo si la cotización no está ya asociada a un usuario
        const isConverted = verification.isConverted;
        setShowCreateAccount(!isConverted);
        
        console.log('Usuario no autenticado, mostrar opción de crear cuenta:', {
          showCreateAccount: !isConverted
        });
        
        // Pre-llenar el email si está disponible en la cotización
        if (result.get('userEmail')) {
          setAccountData(prev => ({
            ...prev,
            email: result.get('userEmail'),
            name: result.get('userName') || ''
          }));
        }
      }
      
      setQuotation(result);
    } catch (err) {
      console.error('Error al obtener detalles de cotización:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la creación de cuenta desde el magic link
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError(null);
    setCreatingAccount(true);
    
    try {
      // Validar datos
      if (!accountData.email || !accountData.password || !accountData.name) {
        setAccountError('Por favor completa todos los campos requeridos');
        return;
      }
      
      if (accountData.password !== accountData.confirmPassword) {
        setAccountError('Las contraseñas no coinciden');
        return;
      }
      
      if (accountData.password.length < 6) {
        setAccountError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      
      console.log('Creando cuenta desde magic link:', {
        magicLink,
        email: accountData.email
      });
      
      const result = await convertQuotationToAccount(magicLink, accountData);
      
      console.log('Cuenta creada exitosamente:', {
        userId: result.user.id,
        isNewUser: result.isNewUser
      });
      
      // Iniciar sesión con el nuevo usuario
      await login(accountData.email, accountData.password);
      
      // Actualizar la cotización en el estado
      setQuotation(result.quotation);
      setShowCreateAccount(false);
      
      // Mostrar mensaje de éxito
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 5000);
    } catch (err) {
      console.error('Error al crear cuenta:', err);
      setAccountError(`Error al crear cuenta: ${err.message}`);
    } finally {
      setCreatingAccount(false);
    }
  };

  useEffect(() => {
    if (magicLink) {
      fetchQuotationDetails();
    }
  }, [magicLink]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <FaSpinner className="spin" size={32} />
        <p className="mt-3">Cargando detalles de la cotización...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="card border-danger">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">Error al cargar la cotización</h5>
          </div>
          <div className="card-body">
            <p className="mb-3">{error}</p>
            <p className="text-muted small mb-3">Si este problema persiste, por favor contacte a soporte técnico.</p>
            <div className="mt-3 d-flex gap-2">
              {isAuthenticated ? (
                <Link to="/dashboard/quotations" className="btn btn-outline-primary">
                  <FaArrowLeft className="me-2" />
                  Volver a mis cotizaciones
                </Link>
              ) : (
                <Link to="/" className="btn btn-outline-primary">
                  <FaArrowLeft className="me-2" />
                  Volver al inicio
                </Link>
              )}
              <button 
                className="btn btn-outline-secondary"
                onClick={() => {
                  // Intentar nuevamente
                  fetchQuotationDetails();
                }}
              >
                <FaSpinner className="me-2" />
                Intentar nuevamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="container py-4">
        <div className="card border-warning">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">Cotización no encontrada</h5>
          </div>
          <div className="card-body">
            <p className="mb-3">La cotización que estás buscando no existe o ha sido eliminada.</p>
            <p className="text-muted small mb-3">Verifica que el enlace sea correcto o busca en tu lista de cotizaciones.</p>
            <div className="mt-3">
              <Link to="/dashboard/quotations" className="btn btn-outline-primary">
                <FaArrowLeft className="me-2" />
                Volver a mis cotizaciones
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const status = quotation.get('status') || 'pending';
  const items = quotation.get('items') || [];
  const createdAt = new Date(quotation.createdAt);
  const updatedAt = new Date(quotation.updatedAt);
  
  // Obtener el historial real de cambios de estado
  const statusHistory = quotation.get('statusHistory') || [];
  
  // Crear un array con el historial completo
  const history = [
    {
      date: createdAt,
      action: 'Cotización creada',
      status: 'pending',
      by: 'system'
    },
    ...statusHistory.map(entry => ({
      date: new Date(entry.date),
      action: `Cotización ${STATUS_LABELS[entry.status] || entry.status}`,
      status: entry.status,
      by: entry.by
    }))
  ];
  
  // Ordenar el historial por fecha
  history.sort((a, b) => a.date - b.date);
  
  // Función para actualizar el estado de la cotización
  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === status) return;
    
    setUpdating(true);
    setUpdateSuccess(false);
    
    try {
      // Actualizar el estado y enviar notificación
      const updatedQuotation = await updateQuotationStatusWithNotification(quotation, newStatus);
      setQuotation(updatedQuotation);
      setUpdateSuccess(true);
      
      // Ocultar el mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      setError('No se pudo actualizar el estado de la cotización');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Detalles de Cotización</h2>
        <Link to="/dashboard/quotations" className="btn btn-outline-primary btn-sm">
          <FaArrowLeft className="me-2" />
          Volver a mis cotizaciones
        </Link>
      </div>

      {/* Información general */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Información General</h5>
          <span className={`badge bg-${STATUS_COLORS[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1"><strong>Fecha de creación:</strong></p>
              <p className="text-muted">{createdAt.toLocaleString()}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Última actualización:</strong></p>
              <p className="text-muted">{updatedAt.toLocaleString()}</p>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-md-6">
              <p className="mb-1"><strong>Email:</strong></p>
              <p className="text-muted">{quotation.get('userEmail')}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Magic Link:</strong></p>
              <code className="small" style={{ wordBreak: 'break-all', display: 'block', fontSize: '0.75rem' }}>{quotation.get('magicLink')}</code>
            </div>
          </div>
        </div>
      </div>

      {/* Productos */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Productos</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Producto</th>
                  <th>Formato</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.selectedFormat?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}</td>
                    <td>{item.bulkQuantity} unidades</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Historial de cambios */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Historial de Cambios</h5>
        </div>
        <div className="card-body">
          <div className="timeline">
            {history.map((event, index) => (
              <div key={index} className="timeline-item">
                <div className="d-flex">
                  <div className="timeline-icon me-3">
                    {event.status === 'pending' && <FaClock className="text-warning" />}
                    {event.status === 'processing' && <FaExclamationTriangle className="text-info" />}
                    {event.status === 'completed' && <FaCheck className="text-success" />}
                  </div>
                  <div className="timeline-content">
                    <p className="mb-0 fw-bold">{event.action}</p>
                    <small className="text-muted">{event.date.toLocaleString()}</small>
                  </div>
                </div>
                {index < history.length - 1 && (
                  <div className="timeline-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Acciones */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Acciones</h5>
        </div>
        <div className="card-body">
          {updateSuccess && (
            <div className="alert alert-success mb-3" role="alert">
              <FaCheck className="me-2" />
              Estado actualizado correctamente. Se ha enviado una notificación por email al usuario.
            </div>
          )}
          
          {/* Formulario para crear cuenta si no está autenticado */}
          {showCreateAccount && !isAuthenticated && (
            <div className="card mb-4 border-primary">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><FaUserPlus className="me-2" /> Crear una cuenta para seguir tu cotización</h5>
              </div>
              <div className="card-body">
                {accountError && (
                  <div className="alert alert-danger">{accountError}</div>
                )}
                <form onSubmit={handleCreateAccount}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      id="email"
                      value={accountData.email}
                      onChange={(e) => setAccountData({...accountData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="row mb-3">
                    <div className="col">
                      <label htmlFor="password" className="form-label">Contraseña</label>
                      <input 
                        type="password" 
                        className="form-control" 
                        id="password"
                        value={accountData.password}
                        onChange={(e) => setAccountData({...accountData, password: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col">
                      <label htmlFor="confirmPassword" className="form-label">Confirmar Contraseña</label>
                      <input 
                        type="password" 
                        className="form-control" 
                        id="confirmPassword"
                        value={accountData.confirmPassword}
                        onChange={(e) => setAccountData({...accountData, confirmPassword: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Nombre</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="name"
                      value={accountData.name}
                      onChange={(e) => setAccountData({...accountData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">Teléfono (opcional)</label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      id="phone"
                      value={accountData.phone}
                      onChange={(e) => setAccountData({...accountData, phone: e.target.value})}
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={creatingAccount}
                  >
                    {creatingAccount ? (
                      <><FaSpinner className="me-2 fa-spin" /> Creando cuenta...</>
                    ) : (
                      <><FaUserPlus className="me-2" /> Crear cuenta</>
                    )}
                  </button>
                  <div className="mt-2 small text-muted">
                    <FaLock className="me-1" /> Tus datos están seguros y solo serán utilizados para gestionar tu cotización.
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {/* Acciones para usuarios normales */}
          <div className="card mb-4 bg-light border-0">
            <div className="card-body">
              <h5 className="mb-3">Acciones</h5>
              <div className="d-flex flex-wrap gap-2">
                <Link to={`/quotation/track/${magicLink}`} className="btn btn-primary">
                  <FaExchangeAlt className="me-1" /> Ver Seguimiento
                </Link>
                
                <a 
                  href={`mailto:info@vvhealth.com?subject=Consulta%20sobre%20cotizaci%C3%B3n%20${quotation.id}`}
                  className="btn btn-outline-primary"
                >
                  <FaEnvelope className="me-1" /> Enviar Email
                </a>
                
                <a href="tel:+17873547839" className="btn btn-outline-primary">
                  <FaPhone className="me-1" /> Llamar
                </a>
                
                {!isAuthenticated && !showCreateAccount && (
                  <button 
                    className="btn btn-success ms-auto"
                    onClick={() => setShowCreateAccount(true)}
                  >
                    <FaUserPlus className="me-1" /> Crear Cuenta
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Acciones para administradores */}
          {isAuthenticated && user && (user.get('role') === 'admin' || user.get('isAdmin') === true) && (
            <div className="card mt-4 border-primary">
              <div className="card-header bg-primary bg-opacity-75 text-white">
                <h5 className="mb-0">Panel de Administrador</h5>
              </div>
              <div className="card-body">
                <h6 className="mb-3">Actualizar Estado de Cotización</h6>
                
                {updateSuccess && (
                  <div className="alert alert-success d-flex align-items-center mb-3">
                    <FaCheck className="me-2" />
                    <div>Estado actualizado correctamente</div>
                  </div>
                )}
                
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button
                    className="btn btn-outline-warning"
                    disabled={status === 'pending' || updating}
                    onClick={() => handleStatusUpdate('pending')}
                  >
                    {updating && status !== 'pending' ? (
                      <><FaSpinner className="me-2 fa-spin" /></>
                    ) : (
                      <><FaClipboardList className="me-1" /></>
                    )}
                    Pendiente
                  </button>
                  
                  <button
                    className="btn btn-outline-info"
                    disabled={status === 'en_proceso' || updating}
                    onClick={() => handleStatusUpdate('en_proceso')}
                  >
                    {updating && status !== 'en_proceso' ? (
                      <><FaSpinner className="me-2 fa-spin" /></>
                    ) : (
                      <><FaFileAlt className="me-1" /></>
                    )}
                    En Proceso
                  </button>
                  
                  <button
                    className="btn btn-outline-info"
                    disabled={status === 'estimado_listo' || updating}
                    onClick={() => handleStatusUpdate('estimado_listo')}
                  >
                    {updating && status !== 'estimado_listo' ? (
                      <><FaSpinner className="me-2 fa-spin" /></>
                    ) : (
                      <><FaCheck className="me-1" /></>
                    )}
                    Estimado Listo
                  </button>
                  
                  <button
                    className="btn btn-outline-success"
                    disabled={status === 'completada' || updating}
                    onClick={() => handleStatusUpdate('completada')}
                  >
                    {updating && status !== 'completada' ? (
                      <><FaSpinner className="me-2 fa-spin" /></>
                    ) : (
                      <><FaShippingFast className="me-1" /></>
                    )}
                    Completada
                  </button>
                  
                  <button
                    className="btn btn-outline-danger"
                    disabled={status === 'rechazada' || updating}
                    onClick={() => handleStatusUpdate('rechazada')}
                  >
                    {updating && status !== 'rechazada' ? (
                      <><FaSpinner className="me-2 fa-spin" /></>
                    ) : (
                      <><FaExclamationTriangle className="me-1" /></>
                    )}
                    Rechazada
                  </button>
                </div>
                
                <div className="alert alert-info d-flex align-items-center mb-0">
                  <FaInfoCircle className="me-2" />
                  <div>
                    <p className="mb-0">Al cambiar el estado, se enviará una notificación por email al usuario utilizando la plantilla de SendGrid correspondiente.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
